import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { Stage, Layer } from 'react-konva'

import { editPhotoActions } from '../../reducers/editPhoto'
import { selectServiceSettings } from '../../selectors'
import { isWatermarkEnabled } from '../../utils/watermark'
import { cursorStyle } from '../../utils/cursorStyles'
import { useDrawingEvents } from './hooks/useDrawingEvents'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'
import { useStageSetup } from './hooks/useStageSetup'
import { useWatermarkLayer } from './hooks/useWatermarkLayer'
import { AIEraserLayer } from './layers/AIEraserLayer'
import { DrawingLayer } from './layers/DrawingLayer'
import { EraserOverlayLayer } from './layers/EraserOverlayLayer'
import { ImageLayers } from './layers/ImageLayers'
import { WatermarkLayer } from './layers/WatermarkLayer'

/**
 * 画像編集用の描画コンポーネント
 * Konva を使用してキャンバス上で消しゴム機能を提供
 */
const Drawing = () => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()

    // Custom Hooks
    const {
        stageWrapRef,
        stageRef,
        outputStageRef,
        previewCanvasRef,
        stageWidth,
        stageHeight,
        outputStageWidth,
        outputStageHeight,
    } = useStageSetup()

    const { handleMouseDown, handleMouseMove, handleMouseUp, showCursor, hideCursor, isDrawing } =
        useDrawingEvents(stageWrapRef)

    useKeyboardShortcuts()
    useWatermarkLayer()

    // Redux State
    const {
        lines,
        showWatermarkInEditor,
        tempAIEraserLines,
        aiEraserLines,
        watermarkLayerRef,
        stage,
        previewCanvas,
        isAIEraserMode,
        curHead,
        curHeadSize,
    } = useSelector((state: any) => state.editPhoto)

    const { beforeImageRef, afterImageRef } = useSelector((state: any) => state.roomDesign)
    const watermarkSettings = useSelector((state: any) => selectServiceSettings(state))

    // ウォーターマーク表示の判定：マイページ設定が有効 かつ 編集画面のチェックボックスがON
    const isWatermarkEnabledFlag = watermarkSettings && isWatermarkEnabled(watermarkSettings) && showWatermarkInEditor

    // 初回描画用（ウォーターマークレイヤーの準備完了も待つ）
    useEffect(() => {
        if (stage && previewCanvas && afterImageRef) {
            // ウォーターマークが有効な場合はレイヤーの準備を待つ
            const shouldWaitForWatermark = watermarkSettings && isWatermarkEnabled(watermarkSettings)

            if (!shouldWaitForWatermark || watermarkLayerRef) {
                setTimeout(() => {
                    dispatch(editPhotoActions.drawPreviewImage({}))
                }, 100)
            }
        }
    }, [afterImageRef, watermarkLayerRef, stage, previewCanvas, watermarkSettings, dispatch])

    // linesが変更されたとき、またはウォーターマーク表示設定が変更されたときの再描画（Undo/Redo含む）
    // ただし描画中は除外
    useEffect(() => {
        if (stage && previewCanvas && !isDrawing.current) {
            // 少し遅延を入れて確実に状態が更新されてから描画
            const timer = setTimeout(() => {
                dispatch(editPhotoActions.drawPreviewImage({}))
            }, 10)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [lines, stage, previewCanvas, dispatch, showWatermarkInEditor, isWatermarkEnabledFlag])

    return (
        <div ref={stageWrapRef} className="drawing-wrapper relative text-center">
            {/* プレビュー用のCanvas */}
            <canvas
                ref={previewCanvasRef}
                width={stageWidth}
                height={stageHeight}
                className="mx-auto"
            />

            {/* AI消しゴムモード時のオーバーレイStage（赤いマスクを直接表示） */}
            {isAIEraserMode && (
                <Stage
                    width={stageWidth}
                    height={stageHeight}
                    className="absolute top-0 left-1/2 -translate-x-1/2"
                    style={{ cursor: cursorStyle(curHead, curHeadSize) }}
                    onMouseEnter={showCursor}
                    onMouseLeave={hideCursor}
                    onMouseDown={handleMouseDown}
                    onMousemove={handleMouseMove}
                    onMouseup={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchMove={handleMouseMove}
                    onTouchEnd={handleMouseUp}
                >
                    {/* AI消しゴムの赤いマスクのみ（React-Konvaの自動レンダリング） */}
                    <AIEraserLayer aiEraserLines={aiEraserLines} tempAIEraserLines={tempAIEraserLines} />
                    {/* ウォーターマークレイヤー（常に最上層に表示） */}
                    <WatermarkLayer
                        watermarkLayerRef={watermarkLayerRef}
                        isVisible={isWatermarkEnabledFlag}
                        width={stageWidth}
                        height={stageHeight}
                    />
                </Stage>
            )}

            {/* 通常消しゴムモード時のオーバーレイStage（Before画像を線の部分だけ表示） */}
            {!isAIEraserMode && beforeImageRef && (
                <Stage
                    width={stageWidth}
                    height={stageHeight}
                    className="absolute top-0 left-1/2 -translate-x-1/2"
                    style={{ cursor: cursorStyle(curHead, curHeadSize) }}
                    onMouseEnter={showCursor}
                    onMouseLeave={hideCursor}
                    onMouseDown={handleMouseDown}
                    onMousemove={handleMouseMove}
                    onMouseup={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchMove={handleMouseMove}
                    onTouchEnd={handleMouseUp}
                >
                    {/* Before画像を線の部分だけ表示（previewCanvasのAfter画像の上に重ねる） */}
                    <EraserOverlayLayer
                        beforeImageRef={beforeImageRef}
                        lines={lines}
                        width={stageWidth}
                        height={stageHeight}
                    />
                    {/* ウォーターマークレイヤー（常に最上層に表示） */}
                    <WatermarkLayer
                        watermarkLayerRef={watermarkLayerRef}
                        isVisible={isWatermarkEnabledFlag}
                        width={stageWidth}
                        height={stageHeight}
                    />
                </Stage>
            )}

            {/* 描画用Stage（非表示） */}
            <Stage
                ref={stageRef}
                width={stageWidth}
                height={stageHeight}
                className="hidden"
                onMouseDown={handleMouseDown}
                onMousemove={handleMouseMove}
                onMouseup={handleMouseUp}
            >
                {/* 通常の消しゴム描画レイヤー */}
                <DrawingLayer lines={lines} />

                {/* AI消しゴム専用レイヤー */}
                <AIEraserLayer aiEraserLines={aiEraserLines} tempAIEraserLines={tempAIEraserLines} />

                {/* Before/After画像レイヤー */}
                <ImageLayers
                    beforeImageRef={beforeImageRef}
                    afterImageRef={afterImageRef}
                    width={stageWidth}
                    height={stageHeight}
                />

                {/* 空のレイヤー（レイヤーインデックスの一致のため） */}
                <Layer />

                {/* ウォーターマークレイヤー */}
                <WatermarkLayer
                    watermarkLayerRef={watermarkLayerRef}
                    isVisible={isWatermarkEnabledFlag}
                    width={stageWidth}
                    height={stageHeight}
                />
            </Stage>

            {/* 出力用Stage（非表示） */}
            <Stage ref={outputStageRef} width={outputStageWidth} height={outputStageHeight} className="hidden">
                {/* 空のレイヤー */}
                <Layer />
                {/* AI消しゴムレイヤー用の空レイヤー（インデックス一致のため） */}
                <Layer />

                {/* Before/After画像レイヤー */}
                <ImageLayers
                    beforeImageRef={beforeImageRef}
                    afterImageRef={afterImageRef}
                    width={outputStageWidth}
                    height={outputStageHeight}
                />

                {/* 空のレイヤー */}
                <Layer />

                {/* ウォーターマークレイヤー */}
                <WatermarkLayer
                    watermarkLayerRef={watermarkLayerRef}
                    isVisible={isWatermarkEnabledFlag}
                    width={outputStageWidth}
                    height={outputStageHeight}
                />
            </Stage>
        </div>
    )
}

export default Drawing
